/**
 * HR's payslip history for one person (Pay & bank tab), across payroll runs and offices:
 * a range of payroll months, each payslip linked, and the same range as Excel.
 */
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { get, qs } from '../lib/api.ts';
import { date, idr, todayIn } from '../lib/format.ts';
import { Button, Chip, Panel, Skeleton, StatusChip } from './ui.tsx';

interface Slip {
  id: string;
  runId: string;
  netPay: string;
  totalEarnings: string;
  totalDeductions: string;
  basicSalary: string;
  isSecondCompany: boolean;
  periodFrom: string;
  periodTo: string;
  transferDate: string | null;
  status: string;
  office: string;
}

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthName = (iso: string) => MONTH.format(new Date(`${iso.slice(0, 7)}-01T00:00:00Z`));

function monthsBack(ym: string, n: number) {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 - n, 1));
  return d.toISOString().slice(0, 7);
}

export function PersonPayslips({ employeeId, name }: { employeeId: string; name: string }) {
  // Up to next month: the period running now ends in it (26 Oct → 25 Nov is November).
  const nextMonth = monthsBack(todayIn().slice(0, 7), -1);
  const [from, setFrom] = useState(monthsBack(nextMonth, 11));
  const [to, setTo] = useState(nextMonth);
  const valid = /^\d{4}-\d{2}$/.test(from) && /^\d{4}-\d{2}$/.test(to) && from <= to;
  const q = useQuery({
    queryKey: ['person-payslips', employeeId, from, to],
    queryFn: () => get<{ payslips: Slip[] }>(`/api/payroll/employees/${employeeId}/payslips${qs({ from, to })}`),
    enabled: valid,
    placeholderData: keepPreviousData,
  });
  const slips = q.data?.payslips ?? [];
  return (
    <Panel
      eyebrow="Payroll"
      title="Payslip history"
      action={
        <div className="row row--wrap" style={{ gap: 6, alignItems: 'center' }}>
          <input className="input" type="month" aria-label="From payroll month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={{ width: 'auto' }} />
          <span className="faint">to</span>
          <input className="input" type="month" aria-label="To payroll month" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ width: 'auto' }} />
          <Button size="sm" icon="download" disabled={!valid || !slips.length} title={`${name}’s payslips for these payroll months, a row each with every earning and deduction`} onClick={() => window.location.assign(`/api/payroll/employees/${employeeId}/payslips.xlsx${qs({ from, to })}`)}>
            Excel
          </Button>
        </div>
      }
      flush
    >
      {!valid ? (
        <p className="faint" style={{ padding: 'var(--s-4)' }}>Choose a first month on or before the last.</p>
      ) : q.isLoading ? (
        <Skeleton h={160} />
      ) : !slips.length ? (
        <p className="faint" style={{ padding: 'var(--s-4)' }}>No payslips for {from === to ? monthName(from) : `${monthName(from)} – ${monthName(to)}`}.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Payroll month</th>
                <th>Period</th>
                <th>Office</th>
                <th>Status</th>
                <th className="right">Basic</th>
                <th className="right">Earnings</th>
                <th className="right">Deductions</th>
                <th className="right">Net pay</th>
              </tr>
            </thead>
            <tbody>
              {slips.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to="/payslips/$id" params={{ id: s.id }} className="link">
                      {monthName(s.periodTo)}
                    </Link>
                    {s.isSecondCompany ? <span style={{ marginLeft: 8 }}><Chip tone="info">Second company</Chip></span> : null}
                  </td>
                  <td className="mono faint" style={{ fontSize: 12 }}>
                    {date(s.periodFrom, { year: false })} → {date(s.periodTo)}
                  </td>
                  <td>{s.office}</td>
                  <td>
                    <StatusChip status={s.status} />
                  </td>
                  <td className="right num">{idr(s.basicSalary)}</td>
                  <td className="right num">{idr(s.totalEarnings)}</td>
                  <td className="right num">{idr(s.totalDeductions)}</td>
                  <td className="right num" style={{ color: 'var(--ink-1)' }}>{idr(s.netPay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
